import { useState } from "react";
import { especiesSugestoes, tiposManejo } from "@/mocks/coletor";

interface MobileTreeFormProps {
  projetoAtivo: { nome: string; responsavel: string };
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
}

const passos = ["Identificação", "GPS", "Espécie", "Medidas", "Fitossanidade", "Risco", "Fotos", "Revisão"];

const tiposIndividuo = ["Árvore", "Palmeira", "Muda", "Arbusto de porte arbóreo", "Toco", "Indivíduo morto", "Não identificado"];

const estadosFitossanitarios = [
  { value: "bom", label: "Bom", bg: "bg-[#CAF0D3]", text: "text-[#1B6E4F]", border: "border-[#1B6E4F]" },
  { value: "regular", label: "Regular", bg: "bg-[#FFFDE7]", text: "text-[#FBC02D]", border: "border-[#FBC02D]" },
  { value: "ruim", label: "Ruim", bg: "bg-[#FFF3E0]", text: "text-[#F57C00]", border: "border-[#F57C00]" },
  { value: "critico", label: "Crítico", bg: "bg-[#FFEBEE]", text: "text-[#D32F2F]", border: "border-[#D32F2F]" },
  { value: "morto", label: "Morto", bg: "bg-[#E8EAED]", text: "text-[#3F4A52]", border: "border-[#3F4A52]" },
];

const niveisRisco = [
  { value: "baixo", label: "Baixo", bg: "bg-[#CAF0D3]", text: "text-[#1B6E4F]", border: "border-[#1B6E4F]" },
  { value: "medio", label: "Médio", bg: "bg-[#FFFDE7]", text: "text-[#FBC02D]", border: "border-[#FBC02D]" },
  { value: "alto", label: "Alto", bg: "bg-[#FFF3E0]", text: "text-[#F57C00]", border: "border-[#F57C00]" },
  { value: "critico", label: "Crítico", bg: "bg-[#FFEBEE]", text: "text-[#D32F2F]", border: "border-[#D32F2F]" },
];

const criteriosRisco = [
  "Risco sobre pedestres", "Risco sobre veículos", "Rede elétrica próxima",
  "Edificação próxima", "Via movimentada", "Escola próxima",
  "Queda iminente", "Solo instável",
];

const problemasFitossanitarios = [
  "Galhos secos", "Cavidade", "Podridão", "Fungos", "Cupins",
  "Inclinação", "Raiz exposta", "Copa desequilibrada",
  "Tronco danificado", "Pragas/doenças",
];

const tiposFoto = ["Geral", "Copa", "Tronco", "Raiz", "Interferência", "Dano", "Antes", "Depois"];

export default function MobileTreeForm({ projetoAtivo, onClose, onSave }: MobileTreeFormProps) {
  const [passo, setPasso] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({
    tipoIndividuo: "",
    codigo: `CTA-GO-5208707-${String(Date.now()).slice(-6)}`,
    responsavel: projetoAtivo.responsavel,
    data: new Date().toISOString(),
    observacoes: "",
    latitude: "",
    longitude: "",
    precisaoGps: "",
    especiePopular: "",
    especieCientifico: "",
    familia: "",
    origem: "",
    porte: "",
    obsBotanicas: "",
    dap: "",
    cap: "",
    alturaTotal: "",
    alturaBifurcacao: "",
    diametroCopa: "",
    obsMedidas: "",
    fitossanitario: "",
    problemas: [] as string[],
    risco: "",
    criteriosRisco: [] as string[],
    justificativaRisco: "",
    fotos: [] as { tipo: string; descricao: string; principal: boolean }[],
    manejoRecomendado: "",
  });

  const [buscaEspecie, setBuscaEspecie] = useState("");
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);

  const updateField = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field: string, item: string) => {
    setFormData((prev) => {
      const arr = (prev[field] as string[]) || [];
      return { ...prev, [field]: arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item] };
    });
  };

  const addFoto = (tipo: string) => {
    const fotos = [...(formData.fotos as { tipo: string; descricao: string; principal: boolean }[]), { tipo, descricao: "", principal: false }];
    updateField("fotos", fotos);
  };

  const removeFoto = (idx: number) => {
    const fotos = [...(formData.fotos as { tipo: string; descricao: string; principal: boolean }[])];
    fotos.splice(idx, 1);
    updateField("fotos", fotos);
  };

  const setFotoPrincipal = (idx: number) => {
    const fotos = (formData.fotos as { tipo: string; descricao: string; principal: boolean }[]).map((f, i) => ({ ...f, principal: i === idx }));
    updateField("fotos", fotos);
  };

  const handleSimularGps = () => {
    updateField("latitude", (-16.6802 + Math.random() * 0.02).toFixed(6));
    updateField("longitude", (-49.2560 + Math.random() * 0.02).toFixed(6));
    updateField("precisaoGps", (3 + Math.random() * 8).toFixed(1));
  };

  const especiesFiltradas = buscaEspecie.length > 1
    ? especiesSugestoes.filter((e) =>
        e.popular.toLowerCase().includes(buscaEspecie.toLowerCase()) ||
        e.cientifico.toLowerCase().includes(buscaEspecie.toLowerCase())
      )
    : [];

  const progresso = Math.round(((passo + 1) / passos.length) * 100);

  const renderStepIndicator = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1.5">
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
          <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
        </button>
        <div className="flex-1">
          <div className="h-2 bg-[#E8EAED] rounded-full overflow-hidden">
            <div className="h-full bg-[#1B6E4F] rounded-full transition-all duration-300" style={{ width: `${progresso}%` }} />
          </div>
        </div>
        <span className="text-xs font-medium text-[#3F4A52]/60 flex-shrink-0">{passo + 1}/{passos.length}</span>
      </div>
      <p className="text-sm font-heading font-bold text-[#3F4A52] ml-10">{passos[passo]}</p>
    </div>
  );

  const renderStep1Identificacao = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Projeto ativo</label>
        <input type="text" value={projetoAtivo.nome} disabled className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/50 text-sm text-[#3F4A52]/70" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Tipo de indivíduo</label>
        <div className="grid grid-cols-2 gap-2">
          {tiposIndividuo.map((t) => {
            const sel = formData.tipoIndividuo === t;
            return (
              <button
                key={t}
                onClick={() => updateField("tipoIndividuo", t)}
                className={`px-3 py-3 rounded-xl border text-sm font-medium text-left transition-all ${
                  sel ? "border-[#1B6E4F] bg-[#CAF0D3] text-[#1B6E4F]" : "border-[#E8EAED] bg-white text-[#3F4A52]"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Código CTA (automático)</label>
        <input type="text" value={formData.codigo as string} disabled className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/50 text-sm text-[#3F4A52]/70 font-mono" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Responsável técnico</label>
        <input type="text" value={formData.responsavel as string} disabled className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/50 text-sm text-[#3F4A52]/70" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Data/hora</label>
        <input type="text" value={new Date((formData.data as string)).toLocaleString("pt-BR")} disabled className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/50 text-sm text-[#3F4A52]/70" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Observações rápidas</label>
        <textarea value={formData.observacoes as string} onChange={(e) => updateField("observacoes", e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] resize-none" placeholder="Ex: Árvore próxima ao poste 42..." />
      </div>
    </div>
  );

  const renderStep2Gps = () => (
    <div className="space-y-4">
      <button onClick={handleSimularGps} className="w-full py-5 rounded-xl bg-[#0052CC] text-white font-heading font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <i className="ri-gps-line text-xl" />
        Capturar localização
      </button>

      <div className="bg-[#64B5F6]/10 border border-[#64B5F6]/30 rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-medium text-[#3F4A52]/60 mb-1">Latitude</label>
            <input type="text" value={formData.latitude as string} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-white text-sm text-[#3F4A52] font-mono text-center" placeholder="--" />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#3F4A52]/60 mb-1">Longitude</label>
            <input type="text" value={formData.longitude as string} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-white text-sm text-[#3F4A52] font-mono text-center" placeholder="--" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#3F4A52]/60 mb-1">Precisão</label>
          <input type="text" value={formData.precisaoGps ? `${formData.precisaoGps} m` : ""} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-white text-sm text-[#3F4A52] font-mono text-center" placeholder="--" />
        </div>
        {(formData.precisaoGps as string) && parseFloat(formData.precisaoGps as string) > 10 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FFF3E0] text-xs font-medium text-[#F57C00]">
            <i className="ri-error-warning-line" />
            Precisão acima de 10 m — considere recapturar o GPS
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={handleSimularGps} className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] hover:bg-[#E8EAED]/50 transition-colors flex items-center justify-center gap-1.5">
          <i className="ri-refresh-line" /> Atualizar GPS
        </button>
        <button onClick={handleSimularGps} className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] hover:bg-[#E8EAED]/50 transition-colors flex items-center justify-center gap-1.5">
          <i className="ri-map-pin-line" /> Selecionar no mapa
        </button>
      </div>

      {/* Mini mapa mockado */}
      <div className="rounded-xl border border-[#E8EAED] overflow-hidden bg-[#E8EAED]/40 h-48 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-map-line text-3xl text-[#3F4A52]/30" />
          <p className="text-xs text-[#3F4A52]/40 mt-1">Mapa de campo</p>
        </div>
      </div>
    </div>
  );

  const renderStep3Especie = () => (
    <div className="space-y-4">
      <div className="relative">
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Buscar espécie</label>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#3F4A52]/40 text-sm" />
          <input
            type="text"
            value={buscaEspecie}
            onChange={(e) => { setBuscaEspecie(e.target.value); setMostrarSugestoes(true); }}
            onFocus={() => setMostrarSugestoes(true)}
            onBlur={() => setTimeout(() => setMostrarSugestoes(false), 200)}
            placeholder="Nome popular ou científico..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F]"
          />
        </div>
        {mostrarSugestoes && especiesFiltradas.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E8EAED] rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {especiesFiltradas.map((e, i) => (
              <button
                key={i}
                onMouseDown={() => {
                  updateField("especiePopular", e.popular);
                  updateField("especieCientifico", e.cientifico);
                  updateField("familia", e.familia);
                  updateField("origem", e.origem);
                  updateField("porte", e.porte);
                  setBuscaEspecie(e.popular);
                  setMostrarSugestoes(false);
                }}
                className="w-full text-left px-3 py-2.5 hover:bg-[#CAF0D3]/30 transition-colors border-b border-[#E8EAED]/50 last:border-0"
              >
                <p className="text-sm font-medium text-[#3F4A52]">{e.popular}</p>
                <p className="text-xs text-[#3F4A52]/50 italic">{e.cientifico} — {e.familia}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => { updateField("especiePopular", "Não identificada"); setBuscaEspecie("Não identificada"); }}
          className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#F57C00] hover:bg-[#FFF3E0] transition-colors"
        >
          Não sei identificar
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Família botânica</label>
          <input type="text" value={formData.familia as string} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/30 text-sm text-[#3F4A52]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Origem</label>
          <input type="text" value={formData.origem as string} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/30 text-sm text-[#3F4A52]" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Porte</label>
        <input type="text" value={formData.porte as string} readOnly className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] bg-[#E8EAED]/30 text-sm text-[#3F4A52]" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Observações botânicas</label>
        <textarea value={formData.obsBotanicas as string} onChange={(e) => updateField("obsBotanicas", e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] resize-none" placeholder="Ex: Floração presente, frutos imaturos..." />
      </div>
    </div>
  );

  const renderStep4Medidas = () => (
    <div className="space-y-4">
      <div className="bg-[#64B5F6]/10 border border-[#64B5F6]/20 rounded-xl p-3 mb-2">
        <p className="text-xs text-[#0052CC] flex items-center gap-1.5">
          <i className="ri-information-line" />
          Preencha apenas os campos disponíveis em campo. Dados incompletos poderão ser revisados depois.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">DAP (cm)</label>
          <input type="number" value={formData.dap as string} onChange={(e) => updateField("dap", e.target.value)} step="0.1" className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] text-center text-lg font-heading font-bold" placeholder="--" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">CAP (cm)</label>
          <input type="number" value={formData.cap as string} onChange={(e) => updateField("cap", e.target.value)} step="0.1" className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] text-center text-lg font-heading font-bold" placeholder="--" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Altura total (m)</label>
          <input type="number" value={formData.alturaTotal as string} onChange={(e) => updateField("alturaTotal", e.target.value)} step="0.1" className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] text-center text-lg font-heading font-bold" placeholder="--" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">1ª bifurcação (m)</label>
          <input type="number" value={formData.alturaBifurcacao as string} onChange={(e) => updateField("alturaBifurcacao", e.target.value)} step="0.1" className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] text-center text-lg font-heading font-bold" placeholder="--" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Diâmetro da copa (m)</label>
        <input type="number" value={formData.diametroCopa as string} onChange={(e) => updateField("diametroCopa", e.target.value)} step="0.1" className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] text-center text-lg font-heading font-bold" placeholder="--" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Observações</label>
        <textarea value={formData.obsMedidas as string} onChange={(e) => updateField("obsMedidas", e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] text-sm text-[#3F4A52] focus:outline-none focus:border-[#1B6E4F] resize-none" />
      </div>
    </div>
  );

  const renderStep5Fitossanidade = () => (
    <div className="space-y-4">
      <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Estado fitossanitário</label>
      <div className="grid grid-cols-3 gap-2">
        {estadosFitossanitarios.map((e) => {
          const sel = formData.fitossanitario === e.value;
          return (
            <button
              key={e.value}
              onClick={() => updateField("fitossanitario", e.value)}
              className={`py-4 rounded-xl border-2 text-sm font-heading font-bold transition-all ${
                sel ? `${e.border} ${e.bg} ${e.text}` : "border-[#E8EAED] bg-white text-[#3F4A52]"
              }`}
            >
              {e.label}
            </button>
          );
        })}
      </div>

      <label className="block text-xs font-medium text-[#3F4A52] mb-2 mt-4">Problemas observados</label>
      <div className="grid grid-cols-2 gap-2">
        {problemasFitossanitarios.map((p) => {
          const sel = (formData.problemas as string[]).includes(p);
          return (
            <button
              key={p}
              onClick={() => toggleArrayItem("problemas", p)}
              className={`px-3 py-3 rounded-xl border text-sm text-left transition-all ${
                sel ? "border-[#F57C00] bg-[#FFF3E0] text-[#F57C00] font-medium" : "border-[#E8EAED] bg-white text-[#3F4A52]"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? "border-[#F57C00] bg-[#F57C00]" : "border-[#E8EAED]"}`}>
                  {sel && <i className="ri-check-line text-white text-[10px]" />}
                </span>
                {p}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep6Risco = () => (
    <div className="space-y-4">
      <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Classificação de risco</label>
      <div className="grid grid-cols-2 gap-2">
        {niveisRisco.map((r) => {
          const sel = formData.risco === r.value;
          return (
            <button
              key={r.value}
              onClick={() => updateField("risco", r.value)}
              className={`py-4 rounded-xl border-2 text-sm font-heading font-bold transition-all ${
                sel ? `${r.border} ${r.bg} ${r.text}` : "border-[#E8EAED] bg-white text-[#3F4A52]"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      <label className="block text-xs font-medium text-[#3F4A52] mb-2 mt-4">Critérios de risco</label>
      <div className="grid grid-cols-2 gap-2">
        {criteriosRisco.map((c) => {
          const sel = (formData.criteriosRisco as string[]).includes(c);
          return (
            <button
              key={c}
              onClick={() => toggleArrayItem("criteriosRisco", c)}
              className={`px-3 py-3 rounded-xl border text-sm text-left transition-all ${
                sel ? "border-[#D32F2F] bg-[#FFEBEE] text-[#D32F2F] font-medium" : "border-[#E8EAED] bg-white text-[#3F4A52]"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? "border-[#D32F2F] bg-[#D32F2F]" : "border-[#E8EAED]"}`}>
                  {sel && <i className="ri-check-line text-white text-[10px]" />}
                </span>
                {c}
              </span>
            </button>
          );
        })}
      </div>

      <div>
        <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Justificativa do risco</label>
        <textarea value={formData.justificativaRisco as string} onChange={(e) => updateField("justificativaRisco", e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-[#E8EAED] text-sm focus:outline-none focus:border-[#1B6E4F] resize-none" placeholder="Descreva os motivos da classificação..." />
      </div>
    </div>
  );

  const renderStep7Fotos = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={() => addFoto("Geral")}
          className="flex-1 py-4 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
        >
          <i className="ri-camera-line text-lg" /> Tirar foto
        </button>
        <button
          onClick={() => addFoto("Geral")}
          className="flex-1 py-4 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] flex items-center justify-center gap-1.5 hover:bg-[#E8EAED]/30 transition-colors"
        >
          <i className="ri-image-line text-lg" /> Galeria
        </button>
      </div>

      {(formData.fotos as { tipo: string; descricao: string; principal: boolean }[]).length > 0 && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-[#3F4A52]">Fotos ({ (formData.fotos as { tipo: string; descricao: string; principal: boolean }[]).length})</label>
          {(formData.fotos as { tipo: string; descricao: string; principal: boolean }[]).map((foto, idx) => (
            <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl border ${foto.principal ? "border-[#1B6E4F] bg-[#CAF0D3]/40" : "border-[#E8EAED] bg-white"}`}>
              <div className="w-12 h-12 rounded-lg bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
                <i className="ri-image-line text-[#3F4A52]/40 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <select
                  value={foto.tipo}
                  onChange={(e) => {
                    const fotos = [...(formData.fotos as { tipo: string; descricao: string; principal: boolean }[])];
                    fotos[idx] = { ...fotos[idx], tipo: e.target.value };
                    updateField("fotos", fotos);
                  }}
                  className="w-full px-2 py-1.5 rounded-lg border border-[#E8EAED] text-xs text-[#3F4A52] bg-white mb-1"
                >
                  {tiposFoto.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  type="text"
                  value={foto.descricao}
                  onChange={(e) => {
                    const fotos = [...(formData.fotos as { tipo: string; descricao: string; principal: boolean }[])];
                    fotos[idx] = { ...fotos[idx], descricao: e.target.value };
                    updateField("fotos", fotos);
                  }}
                  placeholder="Descrição da foto"
                  className="w-full px-2 py-1 rounded-lg border border-[#E8EAED] text-xs text-[#3F4A52]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => setFotoPrincipal(idx)} className={`w-7 h-7 rounded-lg flex items-center justify-center ${foto.principal ? "bg-[#1B6E4F] text-white" : "bg-[#E8EAED] text-[#3F4A52]/60"}`} title="Foto principal">
                  <i className="ri-star-fill text-xs" />
                </button>
                <button onClick={() => removeFoto(idx)} className="w-7 h-7 rounded-lg bg-[#FFEBEE] text-[#D32F2F] flex items-center justify-center">
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep8Revisao = () => (
    <div className="space-y-4">
      <div className="bg-white border border-[#E8EAED] rounded-xl divide-y divide-[#E8EAED]">
        {[
          ["Código CTA", formData.codigo as string],
          ["Projeto", projetoAtivo.nome],
          ["Tipo", formData.tipoIndividuo as string || "—"],
          ["GPS", formData.latitude ? `${formData.latitude}, ${formData.longitude} (±${formData.precisaoGps}m)` : "Não capturado"],
          ["Espécie", (formData.especiePopular as string) || "—"],
          ["Fitossanitário", formData.fitossanitario as string || "—"],
          ["Risco", formData.risco as string || "—"],
          ["DAP", formData.dap ? `${formData.dap} cm` : "—"],
          ["Altura total", formData.alturaTotal ? `${formData.alturaTotal} m` : "—"],
          ["Fotos", `${(formData.fotos as { tipo: string; descricao: string; principal: boolean }[]).length} anexadas`],
        ].map(([label, value], i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3">
            <span className="text-xs text-[#3F4A52]/60">{label}</span>
            <span className={`text-xs font-medium ${value === "—" || value === "Não capturado" ? "text-[#D32F2F]" : "text-[#3F4A52]"}`}>{value}</span>
          </div>
        ))}
      </div>

      {((formData.tipoIndividuo as string) === "" || (formData.fitossanitario as string) === "" || (formData.risco as string) === "") && (
        <div className="bg-[#FFF3E0] border border-[#F57C00]/30 rounded-xl p-3">
          <p className="text-xs font-medium text-[#F57C00] flex items-center gap-1.5">
            <i className="ri-error-warning-line" />
            Pendências: {[
              !formData.tipoIndividuo && "Tipo de indivíduo",
              !formData.fitossanitario && "Estado fitossanitário",
              !formData.risco && "Classificação de risco",
            ].filter(Boolean).join(", ")}
          </p>
        </div>
      )}

      <div className="space-y-2">
        <button
          onClick={() => onSave(formData)}
          className="w-full py-4 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm active:scale-[0.98] transition-transform"
        >
          <i className="ri-save-line mr-1.5" /> Salvar local
        </button>
        <button
          onClick={() => onSave(formData)}
          className="w-full py-4 rounded-xl border-2 border-[#1B6E4F] text-[#1B6E4F] font-heading font-bold text-sm active:scale-[0.98] transition-transform"
        >
          <i className="ri-upload-cloud-2-line mr-1.5" /> Salvar e sincronizar
        </button>
      </div>
    </div>
  );

  const renderPasso = () => {
    switch (passo) {
      case 0: return renderStep1Identificacao();
      case 1: return renderStep2Gps();
      case 2: return renderStep3Especie();
      case 3: return renderStep4Medidas();
      case 4: return renderStep5Fitossanidade();
      case 5: return renderStep6Risco();
      case 6: return renderStep7Fotos();
      case 7: return renderStep8Revisao();
      default: return null;
    }
  };

  return (
    <div className="px-4 pt-4 pb-20 max-w-lg mx-auto">
      {renderStepIndicator()}
      {renderPasso()}

      {/* Botões de navegação (não mostrar na revisão) */}
      {passo < 7 && (
        <div className="flex gap-3 mt-6">
          {passo > 0 && (
            <button onClick={() => setPasso(passo - 1)} className="flex-1 py-3.5 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] hover:bg-[#E8EAED]/30 transition-colors">
              Voltar
            </button>
          )}
          <button onClick={() => setPasso(passo + 1)} className="flex-1 py-3.5 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm active:scale-[0.98] transition-transform">
            Próximo
          </button>
        </div>
      )}
    </div>
  );
}